import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
    explanation: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const TransportChanges: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        setShowExplanation(false);
        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeSocialStudies/TransportChanges.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeSocialStudies/TransportChanges.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setShowExplanation(false);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
        else {
            setShowExplanation(true);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Changes in Transport for Class 3 (CBSE & ICSE) – Social Studies Lesson";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Explore the evolution of transport in this Class 3 Social Studies chapter. Learn about early travel methods, modern vehicles, and how transportation has changed over time for CBSE & ICSE students." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <header>
                                <h1>🚗 Chapter 1: Changes in Transport</h1>
                                <span className="subtitle">How people and goods travel — from animals and carts to planes and rockets</span>
                            </header>

                            <section>
                                <h2>📖 What is Transport?</h2>
                                <p>Transport means moving people or goods from one place to another. Over time, transport has changed a lot — it became faster, safer, and can carry more things.</p>

                                <h2>🐪 Early Modes of Transport</h2>
                                <div className="">
                                    <div className="box">
                                        <h3>On Foot</h3>
                                        <p>Walking was the first way people moved. It is still used for short distances today.</p>
                                    </div>
                                    <div className="box">
                                        <h3>Animals</h3>
                                        <p>People used animals for travel and to carry goods: horses, camels, elephants, and bullocks.</p>
                                    </div>
                                    <div className="box">
                                        <h3>Carts & Sledges</h3>
                                        <p>Wooden carts pulled by animals helped carry heavier loads across villages and towns.</p>
                                    </div>
                                </div>

                                <h2>⛵ Water Transport: Then and Now</h2>
                                <p>Long ago, people used simple dugout boats. Later, they used sailing ships. Today, motor ships and ferries carry people and heavy goods across seas and rivers.</p>
                                <div className="">
                                    <div className="box"><strong>Old:</strong> Wooden boats, sails</div>
                                    <div className="box"><strong>Now:</strong> Motor ships, cargo ships, ferries</div>
                                </div>

                                <h2>🚂 Land Transport: From Wheels to Railways</h2>
                                <p>The wheel was one of the most important inventions. It led to carts and wagons. In the 19th century, the steam engine began the age of railways — trains could carry many people and goods far faster than before. Today we have buses, cars, motorbikes, and high-speed trains.</p>
                                <table className="table table-bordered">
                                    <thead><tr><th>Period</th><th>Common Transport</th></tr></thead>
                                    <tbody>
                                        <tr><td>Ancient</td><td>Walking, animal carts</td></tr>
                                        <tr><td>Medieval</td><td>Horse carriages, sail wagons</td></tr>
                                        <tr><td>Modern</td><td>Trains, cars, buses</td></tr>
                                    </tbody>
                                </table>

                                <h2>✈️ Air Transport: The Dream to Fly</h2>
                                <p>People dreamed of flying for centuries. The Wright brothers made the first successful airplane in 1903. Today planes, helicopters, and even rockets help us travel and send things across the world.</p>

                                <h2>📦 Why Transport Matters</h2>
                                <ul>
                                    <li>Helps people travel for work, school, and holidays</li>
                                    <li>Makes it possible to buy and sell goods from far away</li>
                                    <li>Connects towns, cities, and countries</li>
                                </ul>

                                <h2>🛡️ Safety on the Road</h2>
                                <div className="tips">
                                    <ul>
                                        <li>Wear seat belts in cars and helmets on bikes</li>
                                        <li>Follow traffic lights and road signs</li>
                                        <li>Use zebra crossings to cross roads</li>
                                        <li>Don’t play on the road</li>
                                    </ul>
                                </div>

                                <h2>🌍 Transport and the Environment</h2>
                                <p>Modern transport can cause air and noise pollution. We can help the planet by using public transport, walking or cycling for short trips, and choosing electric vehicles when possible.</p>

                                <h2>📝 Quick Recap</h2>
                                <table className="table table-bordered">
                                    <thead><tr><th>Topic</th><th>Key Point</th></tr></thead>
                                    <tbody>
                                        <tr><td>Early transport</td><td>Walking, animals, carts</td></tr>
                                        <tr><td>Water transport</td><td>From logs to big ships</td></tr>
                                        <tr><td>Land transport</td><td>Wheels → carts → trains → cars</td></tr>
                                        <tr><td>Air transport</td><td>From balloons to airplanes</td></tr>
                                        <tr><td>Safety</td><td>Follow rules and wear helmets</td></tr>
                                        <tr><td>Environment</td><td>Use cleaner transport to reduce pollution</td></tr>
                                    </tbody>
                                </table>

                                <div className="activity">
                                    <h2>🎒 Activities & Questions</h2>
                                    <ul>
                                        <li>Make a timeline showing 4 changes in transport (e.g., walking → cart → train → plane).</li>
                                        <li>Draw your favourite vehicle and write one sentence about how it helps people.</li>
                                        <li>Discuss: How can we reduce pollution caused by vehicles in our town?</li>
                                    </ul>
                                </div>
                            </section>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <header>
                            <h1>🚗 Chapter 1: Changes in Transport</h1>
                            <span className="subtitle">How people and goods travel — from animals and carts to planes and rockets</span>
                        </header>

                        <section>
                            <h2>📖 What is Transport?</h2>
                            <p>Transport means moving people or goods from one place to another. Over time, transport has changed a lot — it became faster, safer, and can carry more things.</p>

                            <h2>🐪 Early Modes of Transport</h2>
                            <div className="">
                                <div className="box">
                                    <h3>On Foot</h3>
                                    <p>Walking was the first way people moved. It is still used for short distances today.</p>
                                </div>
                                <div className="box">
                                    <h3>Animals</h3>
                                    <p>People used animals for travel and to carry goods: horses, camels, elephants, and bullocks.</p>
                                </div>
                                <div className="box">
                                    <h3>Carts & Sledges</h3>
                                    <p>Wooden carts pulled by animals helped carry heavier loads across villages and towns.</p>
                                </div>
                            </div>

                            <h2>⛵ Water Transport: Then and Now</h2>
                            <p>Long ago, people used simple dugout boats. Later, they used sailing ships. Today, motor ships and ferries carry people and heavy goods across seas and rivers.</p>
                            <div className="">
                                <div className="box"><strong>Old:</strong> Wooden boats, sails</div>
                                <div className="box"><strong>Now:</strong> Motor ships, cargo ships, ferries</div>
                            </div>

                            <h2>🚂 Land Transport: From Wheels to Railways</h2>
                            <p>The wheel was one of the most important inventions. It led to carts and wagons. In the 19th century, the steam engine began the age of railways — trains could carry many people and goods far faster than before. Today we have buses, cars, motorbikes, and high-speed trains.</p>
                            <table className="table table-bordered">
                                <thead><tr><th>Period</th><th>Common Transport</th></tr></thead>
                                <tbody>
                                    <tr><td>Ancient</td><td>Walking, animal carts</td></tr>
                                    <tr><td>Medieval</td><td>Horse carriages, sail wagons</td></tr>
                                    <tr><td>Modern</td><td>Trains, cars, buses</td></tr>
                                </tbody>
                            </table>

                            <h2>✈️ Air Transport: The Dream to Fly</h2>
                            <p>People dreamed of flying for centuries. The Wright brothers made the first successful airplane in 1903. Today planes, helicopters, and even rockets help us travel and send things across the world.</p>

                            <h2>📦 Why Transport Matters</h2>
                            <ul>
                                <li>Helps people travel for work, school, and holidays</li>
                                <li>Makes it possible to buy and sell goods from far away</li>
                                <li>Connects towns, cities, and countries</li>
                            </ul>

                            <h2>🛡️ Safety on the Road</h2>
                            <div className="tips">
                                <ul>
                                    <li>Wear seat belts in cars and helmets on bikes</li>
                                    <li>Follow traffic lights and road signs</li>
                                    <li>Use zebra crossings to cross roads</li>
                                    <li>Don’t play on the road</li>
                                </ul>
                            </div>

                            <h2>🌍 Transport and the Environment</h2>
                            <p>Modern transport can cause air and noise pollution. We can help the planet by using public transport, walking or cycling for short trips, and choosing electric vehicles when possible.</p>

                            <h2>📝 Quick Recap</h2>
                            <table className="table table-bordered">
                                <thead><tr><th>Topic</th><th>Key Point</th></tr></thead>
                                <tbody>
                                    <tr><td>Early transport</td><td>Walking, animals, carts</td></tr>
                                    <tr><td>Water transport</td><td>From logs to big ships</td></tr>
                                    <tr><td>Land transport</td><td>Wheels → carts → trains → cars</td></tr>
                                    <tr><td>Air transport</td><td>From balloons to airplanes</td></tr>
                                    <tr><td>Safety</td><td>Follow rules and wear helmets</td></tr>
                                    <tr><td>Environment</td><td>Use cleaner transport to reduce pollution</td></tr>
                                </tbody>
                            </table>

                            <div className="activity">
                                <h2>🎒 Activities & Questions</h2>
                                <ul>
                                    <li>Make a timeline showing 4 changes in transport (e.g., walking → cart → train → plane).</li>
                                    <li>Draw your favourite vehicle and write one sentence about how it helps people.</li>
                                    <li>Discuss: How can we reduce pollution caused by vehicles in our town?</li>
                                </ul>
                            </div>
                        </section>

                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}

                            {(showExplanation) ? (
                                <>
                                    <span><strong>Correct option:</strong> {currentQuestion.answer}</span>
                                    <div dangerouslySetInnerHTML={{ __html: currentQuestion.explanation }}>
                                    </div>
                                    <div className="mt-3 text-center">
                                        <button className="btn hero-btn" onClick={loadRandomSyllabusQuestions}>Next Question</button>
                                    </div>
                                </>
                            ) : (<></>)}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default TransportChanges;
